import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  Avatar,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IGoal from "models/Goal";
import IReview from "models/Reviews";
import fetchApi from "api/fetchApi";
import { useSnackbar } from "hooks/useSnackbar";

interface EvaluationFormProps {
  goals: IGoal[];
  review: IReview;
}

const EvaluationForm: React.FC<EvaluationFormProps> = ({ goals, review }) => {
  const [loading, setLoading] = useState(true);
  const [score, setScore] = useState(0);
  const { showSnackbar, getSnackbarProps } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 1500);
  }, []);

  const groupedGoals = goals.reduce((acc, goal) => {
    const strategicDomain = goal?.key_performance_area?.strategic_domain?.title;

    if (!acc[strategicDomain]) {
      acc[strategicDomain] = [];
    }
    acc[strategicDomain].push(goal);
    return acc;
  }, {});

  const weightedAverage = async () => {
    try {
      const response = await fetchApi(`weighted-average-score/kpis`).get(`/${review.user.id}`);
      const averageScore = response.data.item.weighted_average_score;
      setScore(averageScore.toString());
    } catch (error) {
      console.error("Error fetching Score", error);
    }
  };

  const handleScoreChange = (goal: IGoal, value: number) => {
    const goalId = goal?.id;

    const requestBody = { score: value };

    fetchApi(`kpis/scores/${goalId}`)
      .post("", requestBody)
      .then((response) => {
        if (response.code === 201) {
          weightedAverage();
          setScore(response.data.item.weighted_average_score.toString());
        } else {
          throw new Error("Network response was not ok");
        }
      })
      .catch((error) => {
        console.error("There was a problem with the fetch operation:", error);
      });
  };

  const handleComplete = () => {
    const requestBody = {};

    fetchApi(`reviews/${review.id}`)
      .put("", requestBody)
      .then((response) => {
        if (response.code === 200) {
          console.log("Review Completed");
          showSnackbar("Review completed successfully", "success");

          setTimeout(() => {
            navigate("/");
          }, 1500);
        } else {
          throw new Error("Network response was not ok");
        }
      })
      .catch((error) => {
        console.error("There was a problem with the update operation:", error);
        showSnackbar("An error occured while completeing the review", "error");
      });
  };

  return (
    <>
      <Box display="flex" alignItems="center" mt={4}>
        {loading ? (
          <CircularProgress />
        ) : (
          <Grid container spacing={4}>
            <Grid item xs={12} lg={7}>
              <Box display="flex" flexDirection="column" style={{ flex: 1 }}>
                {/* Accordion here */}
                {Object.keys(groupedGoals).map((strategicDomain) => (
                  <Accordion
                    key={strategicDomain}
                    defaultExpanded={true}
                    sx={{ mb: 1, backgroundColor: "#F6F7FA" }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1a-content"
                      id="panela-header"
                    >
                      <Typography variant="h6" fontSize="24px" color="#146ED2" fontWeight={800}>
                        {strategicDomain}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Box display="flex" flexDirection="column" gap={2}>
                        {groupedGoals[strategicDomain].map((goal: IGoal) => (
                          <Paper
                            key={goal?.id}
                            elevation={3}
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              padding: "10px",
                            }}
                          >
                            <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
                              <Box display="flex" flexDirection="column" style={{ flex: 1 }}>
                                <Box>
                                  <Typography
                                    variant="h6"
                                    fontSize="18px"
                                    color="#146ED2"
                                    fontWeight={800}
                                  >
                                    {goal?.key_performance_area?.title}
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="h6" fontSize="14px">
                                    {goal?.title}
                                  </Typography>
                                </Box>
                              </Box>
                              <RadioGroup row aria-label="rating" defaultValue={0}>
                                {[0, 1, 2, 3, 4].map((value) => (
                                  <FormControlLabel
                                    key={value}
                                    value={value.toString()}
                                    onChange={() => handleScoreChange(goal, value)}
                                    control={
                                      <Radio
                                        sx={{
                                          "& .MuiSvgIcon-root": { fontSize: 18 },
                                          marginLeft: "-16px",
                                          marginRight: "-16px",
                                        }}
                                      />
                                    }
                                    label={value.toString()}
                                    labelPlacement="top"
                                  />
                                ))}
                              </RadioGroup>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
                <Box sx={{ mt: 4 }}>
                  <Paper
                    elevation={3}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      maxWidth: "max-content",
                      padding: "24px",
                    }}
                  >
                    <Typography variant="h6" fontSize="24px" color="#146ED2" fontWeight={800}>
                      Overall Performance Rating
                    </Typography>
                    <Typography
                      variant="h6"
                      fontSize="18px"
                      color="#146ED2"
                      fontWeight={800}
                      sx={{ mb: 2 }}
                    >
                      Weighted average score
                    </Typography>
                    <Typography variant="h6" fontSize="24px" color="#146ED2" fontWeight={800}>
                      {score}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} lg={3}>
              <Box display="flex" flexDirection="column" sx={{ border: "2px solid #E7E7E9" }}>
                <Box sx={{ padding: "10px" }}>
                  <Typography variant="h6" fontSize="16px" fontWeight={800}>
                    Form Author
                  </Typography>
                </Box>
                <Divider />
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{ backgroundColor: "#F6F6F6", padding: "10px" }}
                >
                  <Avatar
                    src={review?.user?.line_manager?.profile_image}
                    alt={`${review?.user?.line_manager?.first_name} ${review?.user?.line_manager?.last_name}`}
                    sx={{ width: 35, height: 35 }}
                  />
                  <Typography variant="h6" fontSize="16px" fontWeight={800}>
                    {`${review?.user?.line_manager?.first_name} ${review?.user?.line_manager?.last_name}`}
                  </Typography>
                </Box>
                <Divider />
                <Box sx={{ padding: "10px" }}>
                  <Typography variant="h6" fontSize="16px" fontWeight={800}>
                    Form Signer
                  </Typography>
                </Box>
                <Divider />
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{ backgroundColor: "#F6F6F6", padding: "10px" }}
                >
                  <Avatar
                    src={review?.user?.profile_image}
                    alt={`${review?.user?.first_name} ${review?.user?.last_name}`}
                    sx={{ width: 35, height: 35 }}
                  />
                  <Typography variant="h6" fontSize="16px" fontWeight={800}>
                    {`${review?.user?.first_name} ${review?.user?.last_name}`}
                  </Typography>
                </Box>
                <Divider />
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                  sx={{ padding: "10px" }}
                >
                  <Typography variant="h6" fontSize="16px" fontWeight={800} textAlign="center">
                    This report will be visible to{" "}
                    {`${review?.user?.first_name} ${review?.user?.last_name}`} when authors/signers
                    are finished
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{ width: "80%", py: 2, borderRadius: "2px" }}
                    onClick={handleComplete}
                  >
                    <Typography variant="h6" fontSize="18px" fontWeight={800}>
                      Complete
                    </Typography>
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
      <Snackbar
        {...getSnackbarProps()}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={4000}
      >
        <Alert {...getSnackbarProps()}>{getSnackbarProps().message}</Alert>
      </Snackbar>
    </>
  );
};

export default EvaluationForm;
